// Microsoft Graph API AI Agents with Structured Prompts
// Implements got-5 nano format for reliable, reproducible API operations
// Each agent has precise tool call definitions for email and calendar management

import { createClient } from '@blinkdotnew/sdk';
import { safeJsonParse } from '@/lib/utils';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

// ==================== STRUCTURED PROMPT FORMATS ====================

/**
 * Got-5 Nano Format: Ultra-precise structured prompts that ensure reliable tool calls
 * Format: [ACTION] [RESOURCE] [PARAMETERS] with strict validation
 */

interface ToolCallDefinition {
  tool: string;
  endpoint: string;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  requiredParams: string[];
  optionalParams: string[];
  permission: string;
  responseFormat: any;
}

// ==================== EMAIL OPERATION AGENTS ====================

export const emailOperationTools: Record<string, ToolCallDefinition> = {
  // Message CRUD
  list_messages: {
    tool: 'list_messages',
    endpoint: '/me/messages',
    method: 'GET',
    requiredParams: ['accessToken'],
    optionalParams: ['$top', '$skip', '$filter', '$select', '$orderby'],
    permission: 'Mail.Read',
    responseFormat: { value: [], '@odata.nextLink': 'string' }
  },
  get_message: {
    tool: 'get_message',
    endpoint: '/me/messages/{id}',
    method: 'GET',
    requiredParams: ['accessToken', 'messageId'],
    optionalParams: ['$select', '$expand'],
    permission: 'Mail.Read',
    responseFormat: { id: 'string', subject: 'string', body: {} }
  },
  create_draft: {
    tool: 'create_draft',
    endpoint: '/me/messages',
    method: 'POST',
    requiredParams: ['accessToken'],
    optionalParams: ['subject', 'body', 'toRecipients', 'ccRecipients', 'bccRecipients'],
    permission: 'Mail.ReadWrite',
    responseFormat: { id: 'string', isDraft: true }
  },
  update_message: {
    tool: 'update_message',
    endpoint: '/me/messages/{id}',
    method: 'PATCH',
    requiredParams: ['accessToken', 'messageId'],
    optionalParams: ['subject', 'body', 'toRecipients', 'isRead', 'categories'],
    permission: 'Mail.ReadWrite',
    responseFormat: { id: 'string', success: true }
  },
  delete_message: {
    tool: 'delete_message',
    endpoint: '/me/messages/{id}',
    method: 'DELETE',
    requiredParams: ['accessToken', 'messageId'],
    optionalParams: [],
    permission: 'Mail.ReadWrite',
    responseFormat: { success: true }
  },
  permanent_delete: {
    tool: 'permanent_delete',
    endpoint: '/me/messages/{id}/permanentDelete',
    method: 'POST',
    requiredParams: ['accessToken', 'messageId'],
    optionalParams: [],
    permission: 'Mail.ReadWrite',
    responseFormat: { success: true }
  },
  
  // Message Actions
  send_mail: {
    tool: 'send_mail',
    endpoint: '/me/sendMail',
    method: 'POST',
    requiredParams: ['accessToken', 'message'],
    optionalParams: ['saveToSentItems'],
    permission: 'Mail.Send',
    responseFormat: { success: true, messageId: 'string' }
  },
  send_draft: {
    tool: 'send_draft',
    endpoint: '/me/messages/{id}/send',
    method: 'POST',
    requiredParams: ['accessToken', 'messageId'],
    optionalParams: [],
    permission: 'Mail.Send',
    responseFormat: { success: true }
  },
  forward_message: {
    tool: 'forward_message',
    endpoint: '/me/messages/{id}/forward',
    method: 'POST',
    requiredParams: ['accessToken', 'messageId', 'toRecipients'],
    optionalParams: ['comment'],
    permission: 'Mail.Send',
    responseFormat: { success: true }
  },
  reply_message: {
    tool: 'reply_message',
    endpoint: '/me/messages/{id}/reply',
    method: 'POST',
    requiredParams: ['accessToken', 'messageId'],
    optionalParams: ['comment'],
    permission: 'Mail.Send',
    responseFormat: { success: true }
  },
  reply_all_message: {
    tool: 'reply_all_message',
    endpoint: '/me/messages/{id}/replyAll',
    method: 'POST',
    requiredParams: ['accessToken', 'messageId'],
    optionalParams: ['comment'],
    permission: 'Mail.Send',
    responseFormat: { success: true }
  },
  create_forward_draft: {
    tool: 'create_forward_draft',
    endpoint: '/me/messages/{id}/createForward',
    method: 'POST',
    requiredParams: ['accessToken', 'messageId'],
    optionalParams: ['toRecipients', 'comment'],
    permission: 'Mail.ReadWrite',
    responseFormat: { id: 'string', isDraft: true }
  },
  create_reply_draft: {
    tool: 'create_reply_draft',
    endpoint: '/me/messages/{id}/createReply',
    method: 'POST',
    requiredParams: ['accessToken', 'messageId'],
    optionalParams: ['comment'],
    permission: 'Mail.ReadWrite',
    responseFormat: { id: 'string', isDraft: true }
  },
  create_reply_all_draft: {
    tool: 'create_reply_all_draft',
    endpoint: '/me/messages/{id}/createReplyAll',
    method: 'POST',
    requiredParams: ['accessToken', 'messageId'],
    optionalParams: ['comment'],
    permission: 'Mail.ReadWrite',
    responseFormat: { id: 'string', isDraft: true }
  },
  
  // Message Management
  copy_message: {
    tool: 'copy_message',
    endpoint: '/me/messages/{id}/copy',
    method: 'POST',
    requiredParams: ['accessToken', 'messageId', 'destinationId'],
    optionalParams: [],
    permission: 'Mail.ReadWrite',
    responseFormat: { id: 'string', success: true }
  },
  move_message: {
    tool: 'move_message',
    endpoint: '/me/messages/{id}/move',
    method: 'POST',
    requiredParams: ['accessToken', 'messageId', 'destinationId'],
    optionalParams: [],
    permission: 'Mail.ReadWrite',
    responseFormat: { id: 'string', success: true }
  },
  get_message_delta: {
    tool: 'get_message_delta',
    endpoint: '/me/mailFolders/{id}/messages/delta',
    method: 'GET',
    requiredParams: ['accessToken', 'folderId'],
    optionalParams: ['$deltaToken', '$skipToken'],
    permission: 'Mail.Read',
    responseFormat: { value: [], '@odata.deltaLink': 'string' }
  },
  
  // Attachments
  list_attachments: {
    tool: 'list_attachments',
    endpoint: '/me/messages/{id}/attachments',
    method: 'GET',
    requiredParams: ['accessToken', 'messageId'],
    optionalParams: ['$select'],
    permission: 'Mail.Read',
    responseFormat: { value: [] }
  },
  add_attachment: {
    tool: 'add_attachment',
    endpoint: '/me/messages/{id}/attachments',
    method: 'POST',
    requiredParams: ['accessToken', 'messageId', 'attachment'],
    optionalParams: [],
    permission: 'Mail.ReadWrite',
    responseFormat: { id: 'string', name: 'string' }
  },
  get_attachment: {
    tool: 'get_attachment',
    endpoint: '/me/messages/{messageId}/attachments/{id}',
    method: 'GET',
    requiredParams: ['accessToken', 'messageId', 'attachmentId'],
    optionalParams: [],
    permission: 'Mail.Read',
    responseFormat: { id: 'string', contentBytes: 'string' }
  },
  delete_attachment: {
    tool: 'delete_attachment',
    endpoint: '/me/messages/{messageId}/attachments/{id}',
    method: 'DELETE',
    requiredParams: ['accessToken', 'messageId', 'attachmentId'],
    optionalParams: [],
    permission: 'Mail.ReadWrite',
    responseFormat: { success: true }
  },
  
  // Mail Folders
  list_folders: {
    tool: 'list_folders',
    endpoint: '/me/mailFolders',
    method: 'GET',
    requiredParams: ['accessToken'],
    optionalParams: ['$select', '$filter'],
    permission: 'Mail.Read',
    responseFormat: { value: [] }
  },
  create_folder: {
    tool: 'create_folder',
    endpoint: '/me/mailFolders',
    method: 'POST',
    requiredParams: ['accessToken', 'displayName'],
    optionalParams: ['isHidden'],
    permission: 'Mail.ReadWrite',
    responseFormat: { id: 'string', displayName: 'string' }
  },
  get_folder: {
    tool: 'get_folder',
    endpoint: '/me/mailFolders/{id}',
    method: 'GET',
    requiredParams: ['accessToken', 'folderId'],
    optionalParams: [],
    permission: 'Mail.Read',
    responseFormat: { id: 'string', displayName: 'string' }
  },
  update_folder: {
    tool: 'update_folder',
    endpoint: '/me/mailFolders/{id}',
    method: 'PATCH',
    requiredParams: ['accessToken', 'folderId'],
    optionalParams: ['displayName', 'isHidden'],
    permission: 'Mail.ReadWrite',
    responseFormat: { id: 'string', success: true }
  },
  delete_folder: {
    tool: 'delete_folder',
    endpoint: '/me/mailFolders/{id}',
    method: 'DELETE',
    requiredParams: ['accessToken', 'folderId'],
    optionalParams: [],
    permission: 'Mail.ReadWrite',
    responseFormat: { success: true }
  },
  copy_folder: {
    tool: 'copy_folder',
    endpoint: '/me/mailFolders/{id}/copy',
    method: 'POST',
    requiredParams: ['accessToken', 'folderId', 'destinationId'],
    optionalParams: [],
    permission: 'Mail.ReadWrite',
    responseFormat: { id: 'string', success: true }
  },
  move_folder: {
    tool: 'move_folder',
    endpoint: '/me/mailFolders/{id}/move',
    method: 'POST',
    requiredParams: ['accessToken', 'folderId', 'destinationId'],
    optionalParams: [],
    permission: 'Mail.ReadWrite',
    responseFormat: { id: 'string', success: true }
  }
};

// ==================== CALENDAR OPERATION AGENTS ====================

export const calendarOperationTools: Record<string, ToolCallDefinition> = {
  // Calendar Management
  list_calendars: {
    tool: 'list_calendars',
    endpoint: '/me/calendars',
    method: 'GET',
    requiredParams: ['accessToken'],
    optionalParams: ['$select'],
    permission: 'Calendars.Read',
    responseFormat: { value: [] }
  },
  create_calendar: {
    tool: 'create_calendar',
    endpoint: '/me/calendars',
    method: 'POST',
    requiredParams: ['accessToken', 'name'],
    optionalParams: ['color'],
    permission: 'Calendars.ReadWrite',
    responseFormat: { id: 'string', name: 'string' }
  },
  get_calendar: {
    tool: 'get_calendar',
    endpoint: '/me/calendars/{id}',
    method: 'GET',
    requiredParams: ['accessToken', 'calendarId'],
    optionalParams: [],
    permission: 'Calendars.Read',
    responseFormat: { id: 'string', name: 'string' }
  },
  update_calendar: {
    tool: 'update_calendar',
    endpoint: '/me/calendars/{id}',
    method: 'PATCH',
    requiredParams: ['accessToken', 'calendarId'],
    optionalParams: ['name', 'color'],
    permission: 'Calendars.ReadWrite',
    responseFormat: { id: 'string', success: true }
  },
  delete_calendar: {
    tool: 'delete_calendar',
    endpoint: '/me/calendars/{id}',
    method: 'DELETE',
    requiredParams: ['accessToken', 'calendarId'],
    optionalParams: [],
    permission: 'Calendars.ReadWrite',
    responseFormat: { success: true }
  },
  
  // Event CRUD
  list_events: {
    tool: 'list_events',
    endpoint: '/me/calendar/events',
    method: 'GET',
    requiredParams: ['accessToken'],
    optionalParams: ['$top', '$skip', '$filter', '$select', '$orderby'],
    permission: 'Calendars.Read',
    responseFormat: { value: [] }
  },
  create_event: {
    tool: 'create_event',
    endpoint: '/me/calendar/events',
    method: 'POST',
    requiredParams: ['accessToken', 'subject', 'start', 'end'],
    optionalParams: ['body', 'location', 'attendees', 'isAllDay', 'recurrence'],
    permission: 'Calendars.ReadWrite',
    responseFormat: { id: 'string', subject: 'string' }
  },
  get_event: {
    tool: 'get_event',
    endpoint: '/me/events/{id}',
    method: 'GET',
    requiredParams: ['accessToken', 'eventId'],
    optionalParams: ['$select'],
    permission: 'Calendars.Read',
    responseFormat: { id: 'string', subject: 'string', start: {}, end: {} }
  },
  update_event: {
    tool: 'update_event',
    endpoint: '/me/events/{id}',
    method: 'PATCH',
    requiredParams: ['accessToken', 'eventId'],
    optionalParams: ['subject', 'start', 'end', 'body', 'location', 'attendees'],
    permission: 'Calendars.ReadWrite',
    responseFormat: { id: 'string', success: true }
  },
  delete_event: {
    tool: 'delete_event',
    endpoint: '/me/events/{id}',
    method: 'DELETE',
    requiredParams: ['accessToken', 'eventId'],
    optionalParams: [],
    permission: 'Calendars.ReadWrite',
    responseFormat: { success: true }
  },
  permanent_delete_event: {
    tool: 'permanent_delete_event',
    endpoint: '/me/events/{id}/permanentDelete',
    method: 'POST',
    requiredParams: ['accessToken', 'eventId'],
    optionalParams: [],
    permission: 'Calendars.ReadWrite',
    responseFormat: { success: true }
  },
  
  // Event Response Actions
  accept_event: {
    tool: 'accept_event',
    endpoint: '/me/events/{id}/accept',
    method: 'POST',
    requiredParams: ['accessToken', 'eventId'],
    optionalParams: ['comment', 'sendResponse'],
    permission: 'Calendars.ReadWrite',
    responseFormat: { success: true }
  },
  tentatively_accept_event: {
    tool: 'tentatively_accept_event',
    endpoint: '/me/events/{id}/tentativelyAccept',
    method: 'POST',
    requiredParams: ['accessToken', 'eventId'],
    optionalParams: ['comment', 'sendResponse', 'proposedNewTime'],
    permission: 'Calendars.ReadWrite',
    responseFormat: { success: true }
  },
  decline_event: {
    tool: 'decline_event',
    endpoint: '/me/events/{id}/decline',
    method: 'POST',
    requiredParams: ['accessToken', 'eventId'],
    optionalParams: ['comment', 'sendResponse', 'proposedNewTime'],
    permission: 'Calendars.ReadWrite',
    responseFormat: { success: true }
  },
  forward_event: {
    tool: 'forward_event',
    endpoint: '/me/events/{id}/forward',
    method: 'POST',
    requiredParams: ['accessToken', 'eventId', 'toRecipients'],
    optionalParams: ['comment'],
    permission: 'Calendars.ReadWrite',
    responseFormat: { success: true }
  },
  cancel_event: {
    tool: 'cancel_event',
    endpoint: '/me/events/{id}/cancel',
    method: 'POST',
    requiredParams: ['accessToken', 'eventId'],
    optionalParams: ['comment'],
    permission: 'Calendars.ReadWrite',
    responseFormat: { success: true }
  },
  
  // Reminder Management
  dismiss_reminder: {
    tool: 'dismiss_reminder',
    endpoint: '/me/events/{id}/dismissReminder',
    method: 'POST',
    requiredParams: ['accessToken', 'eventId'],
    optionalParams: [],
    permission: 'Calendars.ReadWrite',
    responseFormat: { success: true }
  },
  snooze_reminder: {
    tool: 'snooze_reminder',
    endpoint: '/me/events/{id}/snoozeReminder',
    method: 'POST',
    requiredParams: ['accessToken', 'eventId', 'newReminderTime'],
    optionalParams: [],
    permission: 'Calendars.ReadWrite',
    responseFormat: { success: true }
  },
  
  // Event Queries
  list_calendar_view: {
    tool: 'list_calendar_view',
    endpoint: '/me/calendar/calendarView',
    method: 'GET',
    requiredParams: ['accessToken', 'startDateTime', 'endDateTime'],
    optionalParams: ['$select', '$top'],
    permission: 'Calendars.Read',
    responseFormat: { value: [] }
  },
  list_event_instances: {
    tool: 'list_event_instances',
    endpoint: '/me/events/{id}/instances',
    method: 'GET',
    requiredParams: ['accessToken', 'eventId', 'startDateTime', 'endDateTime'],
    optionalParams: [],
    permission: 'Calendars.Read',
    responseFormat: { value: [] }
  },
  get_event_delta: {
    tool: 'get_event_delta',
    endpoint: '/me/calendarView/delta',
    method: 'GET',
    requiredParams: ['accessToken', 'startDateTime', 'endDateTime'],
    optionalParams: ['$deltaToken'],
    permission: 'Calendars.Read',
    responseFormat: { value: [], '@odata.deltaLink': 'string' }
  },
  find_meeting_times: {
    tool: 'find_meeting_times',
    endpoint: '/me/findMeetingTimes',
    method: 'POST',
    requiredParams: ['accessToken'],
    optionalParams: ['attendees', 'timeConstraint', 'meetingDuration'],
    permission: 'Calendars.Read',
    responseFormat: { meetingTimeSuggestions: [] }
  },
  
  // Event Attachments
  list_event_attachments: {
    tool: 'list_event_attachments',
    endpoint: '/me/events/{id}/attachments',
    method: 'GET',
    requiredParams: ['accessToken', 'eventId'],
    optionalParams: [],
    permission: 'Calendars.Read',
    responseFormat: { value: [] }
  },
  add_event_attachment: {
    tool: 'add_event_attachment',
    endpoint: '/me/events/{id}/attachments',
    method: 'POST',
    requiredParams: ['accessToken', 'eventId', 'attachment'],
    optionalParams: [],
    permission: 'Calendars.ReadWrite',
    responseFormat: { id: 'string', name: 'string' }
  },
  get_event_attachment: {
    tool: 'get_event_attachment',
    endpoint: '/me/events/{eventId}/attachments/{id}',
    method: 'GET',
    requiredParams: ['accessToken', 'eventId', 'attachmentId'],
    optionalParams: [],
    permission: 'Calendars.Read',
    responseFormat: { id: 'string', contentBytes: 'string' }
  },
  delete_event_attachment: {
    tool: 'delete_event_attachment',
    endpoint: '/me/events/{eventId}/attachments/{id}',
    method: 'DELETE',
    requiredParams: ['accessToken', 'eventId', 'attachmentId'],
    optionalParams: [],
    permission: 'Calendars.ReadWrite',
    responseFormat: { success: true }
  },
  
  // Calendar Groups
  list_calendar_groups: {
    tool: 'list_calendar_groups',
    endpoint: '/me/calendarGroups',
    method: 'GET',
    requiredParams: ['accessToken'],
    optionalParams: [],
    permission: 'Calendars.Read',
    responseFormat: { value: [] }
  },
  create_calendar_group: {
    tool: 'create_calendar_group',
    endpoint: '/me/calendarGroups',
    method: 'POST',
    requiredParams: ['accessToken', 'name'],
    optionalParams: [],
    permission: 'Calendars.ReadWrite',
    responseFormat: { id: 'string', name: 'string' }
  },
  get_calendar_group: {
    tool: 'get_calendar_group',
    endpoint: '/me/calendarGroups/{id}',
    method: 'GET',
    requiredParams: ['accessToken', 'groupId'],
    optionalParams: [],
    permission: 'Calendars.Read',
    responseFormat: { id: 'string', name: 'string' }
  },
  update_calendar_group: {
    tool: 'update_calendar_group',
    endpoint: '/me/calendarGroups/{id}',
    method: 'PATCH',
    requiredParams: ['accessToken', 'groupId'],
    optionalParams: ['name'],
    permission: 'Calendars.ReadWrite',
    responseFormat: { id: 'string', success: true }
  },
  delete_calendar_group: {
    tool: 'delete_calendar_group',
    endpoint: '/me/calendarGroups/{id}',
    method: 'DELETE',
    requiredParams: ['accessToken', 'groupId'],
    optionalParams: [],
    permission: 'Calendars.ReadWrite',
    responseFormat: { success: true }
  },
  
  // Calendar Permissions
  list_calendar_permissions: {
    tool: 'list_calendar_permissions',
    endpoint: '/me/calendar/calendarPermissions',
    method: 'GET',
    requiredParams: ['accessToken'],
    optionalParams: [],
    permission: 'Calendars.Read',
    responseFormat: { value: [] }
  },
  create_calendar_permission: {
    tool: 'create_calendar_permission',
    endpoint: '/me/calendar/calendarPermissions',
    method: 'POST',
    requiredParams: ['accessToken', 'emailAddress', 'role'],
    optionalParams: [],
    permission: 'Calendars.ReadWrite',
    responseFormat: { id: 'string', role: 'string' }
  },
  get_calendar_permission: {
    tool: 'get_calendar_permission',
    endpoint: '/me/calendar/calendarPermissions/{id}',
    method: 'GET',
    requiredParams: ['accessToken', 'permissionId'],
    optionalParams: [],
    permission: 'Calendars.Read',
    responseFormat: { id: 'string', role: 'string' }
  },
  update_calendar_permission: {
    tool: 'update_calendar_permission',
    endpoint: '/me/calendar/calendarPermissions/{id}',
    method: 'PATCH',
    requiredParams: ['accessToken', 'permissionId'],
    optionalParams: ['role'],
    permission: 'Calendars.ReadWrite',
    responseFormat: { id: 'string', success: true }
  },
  delete_calendar_permission: {
    tool: 'delete_calendar_permission',
    endpoint: '/me/calendar/calendarPermissions/{id}',
    method: 'DELETE',
    requiredParams: ['accessToken', 'permissionId'],
    optionalParams: [],
    permission: 'Calendars.ReadWrite',
    responseFormat: { success: true }
  }
};

// ==================== AI AGENT PROMPT GENERATOR ====================

/**
 * Generate structured prompt for AI to execute Graph API operations
 * Uses got-5 nano format for maximum reliability
 */
export function generateStructuredPrompt(
  operation: string,
  userIntent: string,
  context: Record<string, any>
): string {
  const toolDef = emailOperationTools[operation] || calendarOperationTools[operation];
  
  if (!toolDef) {
    throw new Error(`Unknown operation: ${operation}`);
  }

  return `[EXECUTE_GRAPH_API_OPERATION]

OPERATION: ${operation}
USER_INTENT: ${userIntent}
TOOL: ${toolDef.tool}
METHOD: ${toolDef.method}
ENDPOINT: ${toolDef.endpoint}
PERMISSION_REQUIRED: ${toolDef.permission}

REQUIRED_PARAMETERS:
${toolDef.requiredParams.map(p => `- ${p}: ${context[p] || 'MISSING'}`).join('\n')}

OPTIONAL_PARAMETERS:
${toolDef.optionalParams.map(p => `- ${p}: ${context[p] || 'NOT_PROVIDED'}`).join('\n')}

EXPECTED_RESPONSE_FORMAT:
${JSON.stringify(toolDef.responseFormat, null, 2)}

EXECUTION_STEPS:
1. Validate all REQUIRED_PARAMETERS are present
2. Construct API request with METHOD and ENDPOINT
3. Include Authorization header with accessToken
4. Execute API call to Microsoft Graph
5. Validate response matches EXPECTED_RESPONSE_FORMAT
6. Return structured result

OUTPUT_FORMAT:
{
  "success": boolean,
  "operation": "${operation}",
  "result": <EXPECTED_RESPONSE_FORMAT>,
  "error": string | null
}

EXECUTE NOW.`;
}

// ==================== AGENT EXECUTOR ====================

export class GraphApiAgent {
  private accessToken: string | null = null;

  setAccessToken(token: string) {
    this.accessToken = token;
  }

  /**
   * Execute any Graph API operation with structured prompts
   */
  async execute(
    operation: string,
    userIntent: string,
    parameters: Record<string, any>
  ): Promise<any> {
    if (!this.accessToken) {
      throw new Error('Access token not set. Call setAccessToken() first.');
    }

    // Generate structured prompt
    const prompt = generateStructuredPrompt(operation, userIntent, {
      accessToken: this.accessToken,
      ...parameters
    });

    // Use AI to execute operation with structured prompt
    const { text } = await blink.ai.generateText({
      prompt,
      maxTokens: 500
    });

    // Parse response
    try {
      const result = safeJsonParse(text);
      return result;
    } catch (error) {
      console.error('Failed to parse AI response:', text);
      throw new Error('Invalid AI response format');
    }
  }

  /**
   * Execute operation directly via edge function (bypass AI for speed)
   */
  async executeDirect(
    operation: string,
    parameters: Record<string, any>
  ): Promise<any> {
    const toolDef = emailOperationTools[operation] || calendarOperationTools[operation];
    
    if (!toolDef) {
      throw new Error(`Unknown operation: ${operation}`);
    }

    // Validate required parameters
    for (const param of toolDef.requiredParams) {
      if (param !== 'accessToken' && !parameters[param]) {
        throw new Error(`Missing required parameter: ${param}`);
      }
    }

    // Route to appropriate edge function
    const functionMap: Record<string, string> = {
      send_mail: 'outlook-send',
      create_draft: 'outlook-draft',
      update_message: 'outlook-draft',
      list_messages: 'outlook-search',
      move_message: 'outlook-move',
      copy_message: 'outlook-move'
    };

    const functionName = functionMap[operation];
    
    if (!functionName) {
      // Direct API call for operations without edge function
      return await this.callGraphApiDirectly(toolDef, parameters);
    }

    // Call edge function
    const endpoint = `https://ben3ou87--${functionName}.functions.blink.new`;
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accessToken: this.accessToken,
        ...parameters
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Operation failed');
    }

    return await response.json();
  }

  /**
   * Call Microsoft Graph API directly
   */
  private async callGraphApiDirectly(
    toolDef: ToolCallDefinition,
    parameters: Record<string, any>
  ): Promise<any> {
    let endpoint = `https://graph.microsoft.com/v1.0${toolDef.endpoint}`;
    
    // Replace path parameters
    Object.keys(parameters).forEach(key => {
      endpoint = endpoint.replace(`{${key}}`, parameters[key]);
    });

    // Add query parameters for GET requests
    if (toolDef.method === 'GET') {
      const queryParams = new URLSearchParams();
      toolDef.optionalParams.forEach(param => {
        if (param.startsWith('$') && parameters[param]) {
          queryParams.append(param, parameters[param]);
        }
      });
      if (queryParams.toString()) {
        endpoint += `?${queryParams.toString()}`;
      }
    }

    const options: RequestInit = {
      method: toolDef.method,
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json'
      }
    };

    // Add body for POST/PATCH
    if (['POST', 'PATCH'].includes(toolDef.method)) {
      const body: Record<string, any> = {};
      [...toolDef.requiredParams, ...toolDef.optionalParams].forEach(param => {
        if (param !== 'accessToken' && parameters[param] !== undefined) {
          body[param] = parameters[param];
        }
      });
      options.body = JSON.stringify(body);
    }

    const response = await fetch(endpoint, options);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Graph API call failed');
    }

    // DELETE returns 204 No Content
    if (response.status === 204) {
      return { success: true };
    }

    return await response.json();
  }
}

// Export singleton instance
export const graphApiAgent = new GraphApiAgent();
